/*
 * seo/config.ts — site-level constants and URL helpers for the SEO layer.
 *
 * These values mirror the static base tags in `index.html` (what social crawlers
 * read on a JS-less SPA) and feed the per-route `usePageMeta` hook (what Google
 * and the browser tab read after hydration). Keeping them in one place means the
 * static and dynamic layers never drift apart.
 */

/**
 * Canonical origin of the public site (no trailing slash).
 *
 * A plataforma vive em www.shiba.ia.br desde a virada da Onda W (2026-10-21); o
 * domínio antigo ficou com o blog, que é outro repositório.
 */
export const SITE_URL = 'https://www.shiba.ia.br';

/**
 * Nome da plataforma — o `og:site_name` da base estática e o sufixo de toda aba.
 * É a ÚNICA grafia do nome em código de interface: telas importam esta constante
 * (ou {@link pageTitle}) em vez de repetir o texto, para uma troca de marca ser
 * uma linha.
 */
export const SITE_NAME = 'Shiba IA';

/**
 * Homepage / site-level document title (also the `index.html` default).
 *
 * Kept under {@link MAX_TITLE_CHARS}: Google truncates the SERP title around 60
 * characters, and a cut title loses exactly the distinctive half. Brand first,
 * then the promise — the two parts that must survive.
 */
export const DEFAULT_TITLE = `${SITE_NAME} · IA para a sua base de conhecimento`;

/** SERP budget for a document title — beyond this Google truncates with "…". */
export const MAX_TITLE_CHARS = 60;

/**
 * Site-level description used as the homepage default and OG fallback.
 *
 * Generic on purpose: the platform serves any area or market, so the promise is
 * "your knowledge base", never a vendor ecosystem.
 */
export const DEFAULT_DESCRIPTION =
  `${SITE_NAME} é a plataforma de agentes de IA da sua base de conhecimento: sua equipe ` +
  'responde rápido, gera apresentações e governa dados conforme a LGPD.';

/**
 * Título de aba de uma seção do console ou de página pública: a seção primeiro
 * (é o que distingue uma aba da outra quando várias estão abertas) e a marca no
 * fim. Um formato só, para o separador nunca variar de tela para tela.
 */
export function pageTitle(section: string): string {
  return `${section} · ${SITE_NAME}`;
}

/** SERP budget for a meta description — Google cuts snippets around 160 characters. */
export const MAX_DESCRIPTION_CHARS = 160;

/**
 * Absolute URL of the social/preview image — the 1200×630 card in `public/`.
 *
 * Not `/logo.png`: that one is the brand mark on transparency, sized for the UI,
 * and social crawlers neither compose alpha nor letterbox a square into the
 * landscape slot. The card is the ∫hibΔ wordmark centred on the opaque paper
 * colour at the ratio the previews actually reserve, which is also why the
 * Twitter card below it is `summary_large_image` and not `summary`.
 */
export const DEFAULT_IMAGE = `${SITE_URL}/og.png`;

/**
 * Absolute URL of the brand mark itself — the ∫hibΔ wordmark as a square
 * 512×512 PNG tile on transparency. This is what `Organization.logo` in JSON-LD
 * must carry:
 * search engines render it as the entity's icon, and a 1200×630 card there is
 * cropped to a square. The social card stays `DEFAULT_IMAGE`.
 */
export const BRAND_LOGO = `${SITE_URL}/logo.png`;

/** BCP-47 language tag of the site content. */
export const SITE_LANG = 'pt-BR';

/** Open Graph locale (underscore form). */
export const SITE_LOCALE = 'pt_BR';

/**
 * Resolve a path (or an already-absolute URL) to an absolute URL on the site
 * origin. Idempotent for absolute inputs; guarantees a single slash join.
 */
export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const suffix = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${SITE_URL}${suffix}`;
}
